import * as React from "react";
import { createPortal } from "react-dom";

import { useControlled } from "../../forms/useControlled";
import { useForkRef } from "../../forms/useForkRef";
import { getIntrinsicMeasuredWidth } from "./measurement";
import { TabOverflowList } from "./TabOverflowList";
import { TabSlot } from "./TabSlot";
import { useCollection } from "./useCollection";
import { useOverflow, useOverflowLayoutState, useOverflowSelectionState, useRenderedTabWidth } from "./useOverflow";
import { useRenderedTabsRegistry } from "./useRenderedTabsRegistry";
import {
  TabContext,
  TabListLayoutContext,
  TabSlotRegistryContext,
  TabsContext,
  useTab,
  useTabListLayout,
  useTabs,
  type CollectionItem,
  type RenderedTab,
  type TabsContextValue,
} from "./context";
import "./tabs.css";

/**
 * The Tabs family, replacing `@salt-ds/core`'s behind `tabs.tsx` and `navigation-tab-bar.tsx`.
 *
 * ── HOW A TAB MOVES INTO THE OVERFLOW MENU WITHOUT REMOUNTING ─────────────────────────────
 *
 * Each `Tab` creates a host `div` with `display: contents`, renders its real content into that
 * host through a portal, and leaves a hidden marker `span` where it was authored. `TabList`
 * renders empty SLOTS — in the strip, in the measuring container, in the open menu — and moves
 * hosts between them with `appendChild`.
 *
 * That is not indirection for its own sake. Conditionally rendering a tab in the strip OR the
 * menu unmounts and remounts it, which destroys any state inside it and, more visibly, destroys
 * the element that currently has focus. Moving a live DOM node keeps both.
 *
 * The marker is what preserves ORDER: once hosts are being moved around, a tab's own element no
 * longer says anything about where its author put it.
 *
 * ── WHAT IS NOT PORTED ────────────────────────────────────────────────────────────────────
 *
 * `TabAction` (per-tab action buttons) and the tab-REMOVAL recovery machinery — roughly 380
 * lines of the vendor's total. Nothing in this application removes a tab at runtime or attaches
 * an action to one, and the removal path exists purely to recover focus after a tab that had it
 * disappears. Porting focus-recovery code that no call site can trigger would mean shipping
 * behaviour that could not be verified. Both are recorded rather than silently dropped.
 */

// ─────────────────────────────────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────────────────────────────────

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  defaultValue?: string;
  onChange?: (event: React.SyntheticEvent | null, value: string) => void;
  value?: string;
}

function setValueIdMapEntry(map: Map<string, string>, value: string, id: string) {
  if (map.get(value) === id) return map;
  const next = new Map(map);
  next.set(value, id);
  return next;
}

function removeValueIdMapEntry(map: Map<string, string>, value: string, id: string) {
  // Guarded by id so a replacement that already registered under this value is not deleted by
  // the outgoing tab's cleanup.
  if (map.get(value) !== id) return map;
  const next = new Map(map);
  next.delete(value);
  return next;
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  { children, className, defaultValue, onChange, value, ...rest },
  ref,
) {
  const [valueToTabIdMap, setValueToTabIdMap] = React.useState(() => new Map<string, string>());
  const [valueToPanelIdMap, setValueToPanelIdMap] = React.useState(() => new Map<string, string>());
  const [menuOpen, setMenuOpen] = React.useState(false);
  const activeTab = React.useRef<{ value: string; id: string } | undefined>(undefined);

  /*
   * DESTRUCTURED, not held as objects. Both hooks return a fresh object literal on every render,
   * so listing `collection`/`registry` in the memo below would rebuild the context every time —
   * which changes `registerTab`'s identity, which re-runs every trigger's register effect, which
   * bumps `removalVersion`, which renders again. That is an infinite loop, and it is exactly what
   * the application's own tab tests caught: "Maximum update depth exceeded", 16 of them.
   */
  const {
    getRenderedTab,
    getRenderedTabOrder,
    registerBootstrapTab,
    registerRenderedTab,
    renderMode,
    renderedTabs,
    setBootstrapOverflowReady,
    setBootstrapTabReady,
    updateRenderedTab,
  } = useRenderedTabsRegistry();
  const {
    getFirst,
    getIndex,
    getLast,
    getNext,
    getPrevious,
    item,
    itemAt,
    registerItem,
    sortItems,
    updateItem,
  } = useCollection();

  const [selected, setSelectedState] = useControlled<string | undefined>({
    controlled: value,
    default: defaultValue,
  });

  const commitSelection = React.useCallback(
    (event: React.SyntheticEvent | null, next: string) => {
      setSelectedState(next);
      // Only when it actually changed: re-selecting the current tab is a no-op the caller should
      // not have to filter out of its own handler.
      if (selected !== next) onChange?.(event, next);
    },
    [onChange, selected, setSelectedState],
  );

  const { selectionFromOverflowValueRef, setSelected } = useOverflowSelectionState({
    commitSelection,
    menuOpen,
    setMenuOpen,
  });

  const registerTab = React.useCallback(
    (collectionItem: CollectionItem) => {
      const cleanup = registerItem(collectionItem);
      setValueToTabIdMap((map) => setValueIdMapEntry(map, collectionItem.value, collectionItem.id));
      return () => {
        cleanup();
        setValueToTabIdMap((map) =>
          removeValueIdMapEntry(map, collectionItem.value, collectionItem.id),
        );
      };
    },
    [registerItem],
  );

  const registerPanel = React.useCallback((id: string, panelValue: string) => {
    setValueToPanelIdMap((map) => setValueIdMapEntry(map, panelValue, id));
    return () => setValueToPanelIdMap((map) => removeValueIdMapEntry(map, panelValue, id));
  }, []);

  const getPanelId = React.useCallback(
    (tabValue: string) => valueToPanelIdMap.get(tabValue),
    [valueToPanelIdMap],
  );
  const getTabId = React.useCallback(
    (tabValue: string) => valueToTabIdMap.get(tabValue),
    [valueToTabIdMap],
  );

  const context = React.useMemo<TabsContextValue>(
    () => ({
      activeTab,
      getFirst,
      getIndex,
      getLast,
      getNext,
      getPanelId,
      getPrevious,
      getRenderedTab,
      getRenderedTabOrder,
      getTabId,
      item,
      itemAt,
      menuOpen,
      registerBootstrapTab,
      registerPanel,
      registerRenderedTab,
      registerTab,
      renderMode,
      renderedTabs,
      selected,
      selectionFromOverflowValueRef,
      setBootstrapOverflowReady,
      setBootstrapTabReady,
      setMenuOpen,
      setSelected,
      sortItems,
      updateRenderedTab,
      updateTab: updateItem,
    }),
    [
      getFirst, getIndex, getLast, getNext, getPanelId, getPrevious, getRenderedTab,
      getRenderedTabOrder, getTabId, item, itemAt, menuOpen, registerBootstrapTab, registerPanel,
      registerRenderedTab, registerTab, renderMode, renderedTabs, selected,
      selectionFromOverflowValueRef, setBootstrapOverflowReady, setBootstrapTabReady, setSelected,
      sortItems, updateItem, updateRenderedTab,
    ],
  );

  return (
    <TabsContext.Provider value={context}>
      <div className={["saltTabs", className].filter(Boolean).join(" ")} ref={ref} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
});

// ─────────────────────────────────────────────────────────────────────────────────────────
// TabBar
// ─────────────────────────────────────────────────────────────────────────────────────────

export interface TabBarProps extends React.HTMLAttributes<HTMLDivElement> {
  divider?: boolean;
  inset?: boolean;
}

export const TabBar = React.forwardRef<HTMLDivElement, TabBarProps>(function TabBar(
  { children, className, divider, inset, ...rest },
  ref,
) {
  const classes = ["saltTabBar"];
  if (divider) classes.push("saltTabBar-divider");
  if (inset) classes.push("saltTabBar-inset");
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")} {...rest} ref={ref}>
      {/* The inner strip is what the divider rule underlines; the outer div carries the inset. */}
      <div className="saltTabBar-strip">{children}</div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────────────────
// TabList
// ─────────────────────────────────────────────────────────────────────────────────────────

export interface TabListProps extends React.HTMLAttributes<HTMLDivElement> {
  activeColor?: "primary" | "secondary" | "tertiary";
  appearance?: "bordered" | "transparent";
}

function capitalize(value: string) {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

export const TabList = React.forwardRef<HTMLDivElement, TabListProps>(function TabList(
  { activeColor = "primary", appearance = "bordered", children, className, onKeyDown, ...rest },
  ref,
) {
  const {
    activeTab,
    getFirst,
    getLast,
    getNext,
    getPrevious,
    getRenderedTab,
    menuOpen,
    renderMode,
    renderedTabs,
    selected,
    setBootstrapOverflowReady,
    setMenuOpen,
    sortItems,
  } = useTabs();

  const tabstripRef = React.useRef<HTMLDivElement | null>(null);
  const overflowListRef = React.useRef<HTMLDivElement | null>(null);
  const overflowButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const slotMapRef = React.useRef(new Map<string, HTMLElement>());
  const [slotVersion, setSlotVersion] = React.useState(0);
  const handleRef = useForkRef(tabstripRef, ref);

  const overflowMenuOpen = renderMode === "portal" ? menuOpen : false;

  const [visibleValues, hiddenValues, isMeasuring] = useOverflow({
    container: tabstripRef,
    menuOpen: overflowMenuOpen,
    overflowButton: overflowButtonRef,
    selected,
    tabs: renderedTabs,
  });

  React.useEffect(() => {
    setBootstrapOverflowReady(renderMode === "inline" && renderedTabs.length > 0 && !isMeasuring);
  }, [isMeasuring, renderMode, renderedTabs.length, setBootstrapOverflowReady]);

  const { resolvedOverflowActiveValue, tabListLayoutContext } = useOverflowLayoutState({
    hiddenValues,
    menuOpen,
    overflowMenuOpen,
    visibleValues,
  });

  const registerSlot = React.useCallback((slotId: string, element: HTMLElement | null) => {
    const current = slotMapRef.current.get(slotId) ?? null;
    if (current === element) return;
    if (element) slotMapRef.current.set(slotId, element);
    else slotMapRef.current.delete(slotId);
    // A version bump rather than storing the map in state: the map is read in a layout effect,
    // and putting DOM nodes in state would re-render every tab whenever one slot mounted.
    setSlotVersion((version) => version + 1);
  }, []);

  const slotRegistryContext = React.useMemo(() => ({ registerSlot }), [registerSlot]);

  const slotAssignments = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const value of visibleValues) map.set(value, `main:${value}`);
    // A hidden tab still has to live SOMEWHERE it can be measured — `measure:` is an off-screen
    // container. Leaving it unassigned would detach it and lose its width.
    for (const value of hiddenValues) map.set(value, menuOpen ? `overflow:${value}` : `measure:${value}`);
    return { map, version: slotVersion };
  }, [hiddenValues, menuOpen, slotVersion, visibleValues]);

  React.useLayoutEffect(() => {
    if (renderMode !== "portal") return;
    for (const [value, slotId] of slotAssignments.map) {
      const host = getRenderedTab(value)?.host;
      const slot = slotMapRef.current.get(slotId);
      // The parent check is what makes this idempotent: appending a node that is already there
      // moves it, which would blur whatever inside it currently has focus.
      if (host && slot && host.parentElement !== slot) slot.appendChild(host);
    }
  }, [getRenderedTab, renderMode, slotAssignments]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    // The menu owns its own arrows while it is open; running both would move two highlights.
    if (menuOpen) return;
    const action = { ArrowRight: getNext, ArrowLeft: getPrevious, Home: getFirst, End: getLast }[
      event.key
    ];
    if (!action) return;
    event.preventDefault();
    // Sorted first: a tab may have moved between the strip and the menu since the last sort,
    // and navigating a stale order jumps to whatever used to be next.
    sortItems();
    const activeTabId = activeTab.current?.id;
    if (!activeTabId) return;
    // `preventScroll` because focusing a tab at the far end of a wide strip would otherwise
    // scroll the whole page sideways.
    action(activeTabId)?.element?.focus({ preventScroll: true });
  };

  const classes = [
    "saltTabList",
    `saltTabList-${appearance}`,
    "saltTabList-horizontal",
    `saltTabList-activeColor${capitalize(activeColor)}`,
  ];
  if (className) classes.push(className);

  return (
    <div
      className={classes.join(" ")}
      // Only in portal mode: during the inline bootstrap the tabs ARE the layout, and hiding
      // them would flash an empty bar on first paint.
      data-ismeasuring={renderMode === "portal" && isMeasuring ? true : undefined}
      onKeyDown={handleKeyDown}
      ref={handleRef}
      role="tablist"
      {...rest}
    >
      {renderMode === "inline" ? (
        children
      ) : (
        <TabSlotRegistryContext.Provider value={slotRegistryContext}>
          <TabListLayoutContext.Provider value={tabListLayoutContext}>
            {children}
            {visibleValues.map((value) => (
              <TabSlot key={value} slotId={`main:${value}`} value={value} />
            ))}
            {!menuOpen && hiddenValues.length > 0 ? (
              <div aria-hidden="true" className="saltTabList-measureContainer" role="presentation">
                {hiddenValues.map((value) => (
                  <TabSlot key={`measure-${value}`} slotId={`measure:${value}`} value={value} />
                ))}
              </div>
            ) : null}
            <TabOverflowList
              buttonRef={overflowButtonRef}
              hiddenValues={hiddenValues}
              open={menuOpen}
              order={renderedTabs.length}
              ref={overflowListRef}
              setOpen={setMenuOpen}
            />
          </TabListLayoutContext.Provider>
        </TabSlotRegistryContext.Provider>
      )}
      <TabSelectionAnnouncer
        resolvedOverflowActiveValue={resolvedOverflowActiveValue}
        selected={selected}
      />
    </div>
  );
});

/**
 * ONE live region for the whole document, created lazily and shared.
 *
 * Two reasons it is not rendered inside the tablist. A `role="tablist"` may contain tabs — a
 * stray span in there is a node an assistive technology has to reason about and a node the
 * strip's flex layout has to lay out. And a page with three tab bars would otherwise have three
 * live regions, so a single announcement could be read three times.
 *
 * This is the shape Salt gets from its `AriaAnnouncerProvider` at the app root; ours is
 * self-contained so the design system does not depend on a provider the application may not
 * mount.
 */
function getSharedAnnouncerHost(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const existing = document.getElementById("ds-tabs-announcer");
  if (existing) return existing;
  const host = document.createElement("span");
  host.id = "ds-tabs-announcer";
  host.setAttribute("aria-live", "polite");
  host.className = "saltTabList-announcer";
  document.body.appendChild(host);
  return host;
}

/**
 * Announce a tab that has just been pulled out of the overflow menu.
 *
 * A selection made inside the menu changes the bar's layout — the chosen tab appears in the
 * strip — and that rearrangement is invisible to a screen reader. Its own live region rather
 * than Salt's announcer, so the design system does not depend on a provider the app may not
 * mount.
 */
function TabSelectionAnnouncer({
  resolvedOverflowActiveValue,
  selected,
}: {
  resolvedOverflowActiveValue: string | null;
  selected: string | undefined;
}) {
  const { getRenderedTab, selectionFromOverflowValueRef } = useTabs();
  const [message, setMessage] = React.useState("");
  const previousSelected = React.useRef(selected);

  React.useEffect(() => {
    const fromOverflow = selectionFromOverflowValueRef.current;
    const changed = selected !== previousSelected.current;
    previousSelected.current = selected;
    if (!changed || fromOverflow == null || selected === undefined) return;
    if (selected !== fromOverflow) {
      selectionFromOverflowValueRef.current = null;
      return;
    }
    const label = getRenderedTab(selected)?.trigger?.textContent?.trim() || selected;
    setMessage(`${label} moved to main tab list`);
    selectionFromOverflowValueRef.current = null;
  }, [getRenderedTab, selected, selectionFromOverflowValueRef]);

  const [host, setHost] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => setHost(getSharedAnnouncerHost()), []);
  if (!host) return null;
  /*
   * Portalled out of the tablist entirely. `aria-live` is `polite` on the host: this narrates a
   * consequence of the user's own action, and interrupting them to describe what they just did
   * is worse than saying it a moment later.
   *
   * `resolvedOverflowActiveValue` is accepted but not rendered — it drives focus in the menu,
   * and announcing every highlight change would talk over a user simply arrowing through a list.
   */
  void resolvedOverflowActiveValue;
  return createPortal(message, host);
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Tab
// ─────────────────────────────────────────────────────────────────────────────────────────

export interface TabProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  value: string;
}

export const Tab = React.forwardRef<HTMLDivElement, TabProps>(function Tab(
  { children, className, disabled: disabledProp, id: idProp, onBlur, onFocus, onFocusCapture, onMouseDown, value, ...rest },
  ref,
) {
  const {
    activeTab,
    registerBootstrapTab,
    registerRenderedTab,
    renderMode,
    selected,
    setBootstrapTabReady,
    updateRenderedTab,
  } = useTabs();

  const disabled = !!disabledProp;
  const reactId = React.useId();
  const id = idProp ?? reactId;
  const wasMouseDown = React.useRef(false);
  const [focusVisible, setFocusVisible] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [hostElement, setHostElement] = React.useState<HTMLElement | null>(null);
  const markerRef = React.useRef<HTMLSpanElement | null>(null);
  const tabRootRef = React.useRef<HTMLDivElement | null>(null);
  const [actionIds, setActionIds] = React.useState(() => new Set<string>());

  const registerAction = React.useCallback((actionId: string) => {
    setActionIds((old) => {
      if (old.has(actionId)) return old;
      const next = new Set(old);
      next.add(actionId);
      return next;
    });
    return () => {
      setActionIds((old) => {
        if (!old.has(actionId)) return old;
        const next = new Set(old);
        next.delete(actionId);
        return next;
      });
    };
  }, []);

  const actions = React.useMemo(() => Array.from(actionIds), [actionIds]);
  const context = React.useMemo(
    () => ({ actions, disabled, focused, registerAction, selected: selected === value, tabId: id, value }),
    [actions, disabled, focused, id, registerAction, selected, value],
  );

  React.useLayoutEffect(() => {
    // Built by hand rather than rendered, because React must not own it: the list moves this
    // node between slots, and React would move it back on the next render.
    const host = document.createElement("div");
    host.dataset.tabHost = value;
    host.role = "presentation";
    // `contents` so the host adds no box — otherwise every tab gains a wrapper and the strip's
    // flex layout measures the wrapper instead of the tab.
    host.style.display = "contents";
    setHostElement(host);
    return () => host.remove();
  }, [value]);

  React.useLayoutEffect(() => {
    if (renderMode !== "inline") return;
    return registerBootstrapTab(value);
  }, [registerBootstrapTab, renderMode, value]);

  React.useLayoutEffect(() => {
    setBootstrapTabReady(value, hostElement != null);
    return () => setBootstrapTabReady(value, false);
  }, [hostElement, setBootstrapTabReady, value]);

  React.useLayoutEffect(() => {
    if (!hostElement) return;
    return registerRenderedTab({
      host: hostElement,
      id,
      marker: markerRef.current,
      root: tabRootRef.current,
      trigger: null,
      value,
      width: getIntrinsicMeasuredWidth(tabRootRef.current),
    } satisfies RenderedTab);
  }, [hostElement, id, registerRenderedTab, value]);

  React.useLayoutEffect(() => {
    const updates: Partial<RenderedTab> = { marker: markerRef.current, root: tabRootRef.current };
    // The width is only re-read in inline mode: in portal mode the tab may currently be sitting
    // in the menu or the measuring container, where its width means nothing.
    if (renderMode === "inline") updates.width = getIntrinsicMeasuredWidth(tabRootRef.current);
    updateRenderedTab(value, updates);
  }, [renderMode, updateRenderedTab, value]);

  useRenderedTabWidth({ hostElement, renderMode, tabRootRef, updateRenderedTab, value });

  const handleTabRootRef = useForkRef(tabRootRef, ref);

  const classes = ["saltTab"];
  if (selected === value) classes.push("saltTab-selected");
  if (disabled) classes.push("saltTab-disabled");
  if (focusVisible) classes.push("saltTab-focusVisible");
  if (className) classes.push(className);

  const tabMarkup = (
    <TabContext.Provider value={context}>
      <div
        className={classes.join(" ")}
        data-overflowitem="true"
        data-value={value}
        onBlur={(event) => {
          onBlur?.(event);
          setFocused(false);
          setFocusVisible(false);
        }}
        onFocus={(event) => {
          onFocus?.(event);
          setFocused(true);
          // The focus RING only for keyboard focus. A mouse press focuses the tab too, and
          // drawing a ring there is the "why is this outlined" bug.
          if (!wasMouseDown.current && (event.target as HTMLElement).getAttribute("role") === "tab") {
            setFocusVisible(true);
          }
          wasMouseDown.current = false;
        }}
        onFocusCapture={(event) => {
          onFocusCapture?.(event);
          // Captured, not bubbled: the focus lands on the TRIGGER inside, and the list needs to
          // know which tab that was before any other handler runs.
          activeTab.current = { id, value };
        }}
        onMouseDown={(event) => {
          onMouseDown?.(event);
          activeTab.current = { id, value };
          wasMouseDown.current = true;
        }}
        ref={handleTabRootRef}
        role="presentation"
        {...rest}
      >
        {children}
      </div>
    </TabContext.Provider>
  );

  return (
    <>
      {/* Stays put while the host moves; it is the only record of authoring order. */}
      <span data-tab-marker="" hidden ref={markerRef} role="presentation" />
      {renderMode === "inline" ? tabMarkup : hostElement ? createPortal(tabMarkup, hostElement) : null}
    </>
  );
});

// ─────────────────────────────────────────────────────────────────────────────────────────
// TabTrigger
// ─────────────────────────────────────────────────────────────────────────────────────────

export interface TabTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function getAriaDescription(count: number) {
  if (count < 1) return undefined;
  return count === 1 ? "1 action available" : `${count} actions available`;
}

export const TabTrigger = React.forwardRef<HTMLButtonElement, TabTriggerProps>(function TabTrigger(
  { children, className, onClick, onFocus, onKeyDown, ...rest },
  ref,
) {
  const {
    activeTab,
    getPanelId,
    getRenderedTabOrder,
    getTabId,
    menuOpen,
    registerTab,
    selected: selectedValue,
    setMenuOpen,
    setSelected,
    updateRenderedTab,
    updateTab,
  } = useTabs();
  const { actions, disabled, focused, selected, tabId, value } = useTab();
  const tabListLayout = useTabListLayout();
  const tabRef = React.useRef<HTMLButtonElement | null>(null);

  const location = tabListLayout?.getLocation(value) ?? "main";
  const selectionSource = location === "overflow" ? "overflow" : "main";
  const hidden = location === "hidden";
  const overflowOpen = location === "overflow" && menuOpen;
  const renderOrder = getRenderedTabOrder(value);
  const order = renderOrder >= 0 ? renderOrder : undefined;
  const initialLocationRef = React.useRef(location);
  const initialOrderRef = React.useRef(order);

  React.useLayoutEffect(() => {
    if (!tabId || !tabRef.current) return;
    return registerTab({
      element: tabRef.current,
      id: tabId,
      location: initialLocationRef.current,
      order: initialOrderRef.current,
      value,
    });
  }, [registerTab, tabId, value]);

  React.useLayoutEffect(() => {
    if (!tabId) return;
    updateTab(tabId, { element: tabRef.current, location, order });
  }, [location, order, tabId, updateTab]);

  React.useLayoutEffect(() => {
    updateRenderedTab(value, { trigger: tabRef.current });
  }, [updateRenderedTab, value]);

  React.useLayoutEffect(() => {
    if (!overflowOpen || tabListLayout?.overflowActiveValue !== value) return;
    tabRef.current?.focus({ preventScroll: true });
  }, [overflowOpen, tabListLayout?.overflowActiveValue, value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);

    if (location === "overflow" && event.key === "Tab" && event.shiftKey) {
      event.preventDefault();
      setMenuOpen(false);
      const trigger = event.currentTarget.ownerDocument.querySelector<HTMLElement>(
        "[data-overflowbutton]",
      );
      // Deferred a frame: the menu is closing, and focusing the button before it has gone puts
      // focus on an element about to be re-laid-out.
      requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
      return;
    }

    if (
      location === "overflow" &&
      tabListLayout &&
      ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
    ) {
      // Vertical keys inside the menu, horizontal keys in the strip — the two lists have
      // different orientations and the same tab is in both.
      if (tabListLayout.moveOverflowFocus(event.key, value)) {
        event.preventDefault();
        return;
      }
    }

    if (disabled) {
      // Still swallowed for a disabled tab, so the key does not reach the page and scroll it.
      if (event.key === "Enter" || event.key === " ") event.preventDefault();
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setSelected(event, value, selectionSource);
    }
  };

  const panelId = getPanelId(value);
  const active = location === "overflow" && tabListLayout?.overflowActiveValue === value;
  const hasSelectedTab = selectedValue !== undefined && getTabId(selectedValue) != null;
  /*
   * With nothing selected, the FIRST tab is the tab stop. Otherwise a tablist with no selection
   * would have no tab stop at all and Tab would skip the whole bar.
   */
  const fallbackTabStop = !hasSelectedTab && location === "main" && order === 0;
  const isTabStop = !hidden && (focused || selected || active || fallbackTabStop);

  return (
    <button
      aria-controls={panelId}
      aria-description={getAriaDescription(actions.length)}
      aria-disabled={disabled}
      aria-selected={selected}
      className={["saltTabTrigger", className].filter(Boolean).join(" ")}
      data-value={value}
      id={tabId}
      onClick={
        !disabled
          ? (event) => {
              onClick?.(event);
              setSelected(event, value, selectionSource);
            }
          : undefined
      }
      onFocus={(event) => {
        onFocus?.(event);
        if (tabId) activeTab.current = { id: tabId, value };
        if (location === "overflow") tabListLayout?.setOverflowActiveValue(value);
        // `nearest` so a tab reached by keyboard scrolls into view without yanking the page.
        event.currentTarget.parentElement?.scrollIntoView({ block: "nearest", inline: "nearest" });
      }}
      // Attached even when disabled IF the tab is in the menu, because the menu's own navigation
      // keys have to keep working across a disabled entry.
      onKeyDown={location === "overflow" || !disabled ? handleKeyDown : undefined}
      ref={useForkRef(tabRef, ref)}
      role="tab"
      tabIndex={isTabStop ? 0 : -1}
      type="button"
      {...rest}
    >
      {children}
    </button>
  );
});

// ─────────────────────────────────────────────────────────────────────────────────────────
// TabPanel
// ─────────────────────────────────────────────────────────────────────────────────────────

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

export const TabPanel = React.forwardRef<HTMLDivElement, TabPanelProps>(function TabPanel(
  { children, className, id: idProp, value, ...rest },
  ref,
) {
  const { getTabId, registerPanel, selected } = useTabs();
  const reactId = React.useId();
  const id = idProp ?? reactId;
  const hidden = selected !== value;
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const handleRef = useForkRef(panelRef, ref);
  const [hasFocusableChildren, setHasFocusableChildren] = React.useState(false);

  React.useLayoutEffect(() => registerPanel(id, value), [id, registerPanel, value]);

  React.useEffect(() => {
    const element = panelRef.current;
    if (!element || hidden) return;
    /*
     * A panel with nothing focusable inside must itself be focusable, or a keyboard user pressing
     * Tab from the tab strip lands past the panel entirely and never reaches its content. A panel
     * that DOES contain focusable children must not be, or it becomes a pointless extra stop.
     *
     * Its content can change at any time, so this is observed rather than computed once.
     */
    const detect = () => {
      const focusable = element.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      setHasFocusableChildren((previous) =>
        previous === focusable.length > 0 ? previous : focusable.length > 0,
      );
    };
    let frame: number | null = null;
    const schedule = () => {
      if (frame != null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        detect();
      });
    };
    schedule();
    const observer =
      typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
    observer?.observe(element, { attributes: true, childList: true, subtree: true });
    return () => {
      observer?.disconnect();
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [hidden]);

  return (
    <div
      aria-labelledby={getTabId(value)}
      className={["saltTabPanel", className].filter(Boolean).join(" ")}
      hidden={hidden || undefined}
      id={id}
      ref={handleRef}
      role="tabpanel"
      tabIndex={hidden || hasFocusableChildren ? undefined : 0}
      {...rest}
    >
      {children}
    </div>
  );
});
